import { CreditCard } from "@phosphor-icons/react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

/**
 * Card entry tab of the Pay wizard. Handles both keyed-manual entry
 * and MSR-swiped tracks (surfaced via ``swipeTracks``). Kept as a
 * "dumb" presentational component — all card-tokenization and
 * gateway calls stay in ``PaymentModal.jsx`` so we only shuffle JSX
 * out of the parent, not payment logic.
 */
const CardPanel = ({
  swipeTracks,
  clearCardFields,
  firstName, setFirstName,
  lastName, setLastName,
  cardNumber, setCardNumber,
  expDate, setExpDate,
  cvv, setCvv,
  formatCardNumber,
  formatExpDate,
}) => (
  <div className="space-y-3">
    {swipeTracks ? (
      <div
        className="rounded-md border-2 border-green-400 bg-green-50 dark:bg-green-950/30 dark:border-green-700 px-3 py-2 text-sm flex items-center gap-2"
        data-testid="card-swiped-chip"
      >
        <span className="inline-block h-2 w-2 rounded-full bg-green-500" />
        <span className="font-semibold text-green-800 dark:text-green-200">SWIPED</span>
        <span className="text-green-900/80 dark:text-green-200/80">
          · Card present, no CVV needed. Press Pay to tokenize &amp; charge.
        </span>
        <button
          type="button"
          onClick={clearCardFields}
          className="ml-auto text-xs text-green-700 dark:text-green-300 underline underline-offset-2"
          data-testid="card-clear-swipe"
        >
          Clear
        </button>
      </div>
    ) : (
      <div
        className="rounded-md border border-dashed border-primary/40 bg-primary/5 px-3 py-2 text-xs text-primary flex items-center gap-2"
        data-testid="card-swipe-hint"
      >
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
        Ready for swipe — swipe a card now or fill in the fields manually below.
      </div>
    )}
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1">
        <Label>First Name</Label>
        <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="card-first-name" />
      </div>
      <div className="space-y-1">
        <Label>Last Name</Label>
        <Input value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid="card-last-name" />
      </div>
    </div>
    <div className="space-y-1">
      <Label>Card Number</Label>
      <Input
        placeholder="4111 1111 1111 1111"
        value={cardNumber}
        onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
        maxLength={19}
        className="font-mono"
        data-testid="card-number"
      />
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1">
        <Label>Expiration</Label>
        <Input
          placeholder="MM/YY"
          value={expDate}
          onChange={(e) => setExpDate(formatExpDate(e.target.value))}
          maxLength={5}
          className="font-mono"
          data-testid="card-exp"
        />
      </div>
      <div className="space-y-1">
        <Label>CVV</Label>
        <Input
          placeholder="123"
          type="password"
          value={cvv}
          onChange={(e) => setCvv(e.target.value.replace(/\D/g, "").substring(0, 4))}
          maxLength={4}
          className="font-mono"
          data-testid="card-cvv"
        />
      </div>
    </div>
  </div>
);

/** Pinpad prompt panel — shown when an external terminal is configured. */
export const PinpadPanel = ({ total, terminalType }) => (
  <div className="text-center py-6" data-testid="pinpad-panel">
    <CreditCard size={40} weight="duotone" className="mx-auto mb-2 text-primary" />
    <p className="font-medium">Charge ${total.toFixed(2)} on the pinpad</p>
    <p className="text-xs text-muted-foreground mt-1">
      {terminalType === "authnet_semi_integrated" && "Authorize.net Semi-Integrated"}
      {terminalType === "generic_http" && "Generic HTTP pinpad"}
      {terminalType === "test_mode" && "TEST MODE (no real charge)"}
      {" — "}Press <span className="font-semibold text-foreground">Send to Pinpad</span> below,
      then hand the terminal to the customer.
    </p>
  </div>
);

export default CardPanel;
